"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.calculateLatestHumidityValue = exports.useLatestHumidityFromEvents = exports.formatHumidityChartData = void 0;
const react_1 = require("react");
const index_1 = require("../../index");
const pacificTime_1 = require("../pacificTime");
const formatHumidityChartData = (leakDetectorRollupData) => {
    if (!leakDetectorRollupData)
        return [];
    const dayMap = new Map();
    leakDetectorRollupData.forEach((day) => {
        // day.label is already the rollup's Pacific YYYY-MM-DD key; round-tripping it
        // through Date() parsed it as UTC midnight and shifted it a day west.
        const dateKey = day.label;
        if (!dayMap.has(dateKey)) {
            dayMap.set(dateKey, []);
        }
        dayMap.get(dateKey)?.push(day);
    });
    const chartData = [];
    // last 7 days
    for (let i = 6; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        // Rollup rows are Pacific-keyed server-side, so "the last 7 days" has to be
        // resolved in Pacific too — a device-local key misses every row east of PT.
        const dateKey = (0, pacificTime_1.pacificDateString)(date);
        const dayLabel = (0, pacificTime_1.weekdayLabelForDateKey)(dateKey);
        const dayReadings = dayMap.get(dateKey) || [];
        if (dayReadings.length === 0) {
            chartData.push({
                value: null,
                label: dayLabel,
                timestamp: date.getTime(),
                error: false,
            });
        }
        else {
            // ponytail: this 7-day chart is fed the 24-hour hourly array, and every row
            // in a bucket shares one date label, so this sort is a no-op and "latest"
            // is really "first hour returned". Separate bug — left alone deliberately.
            // Upgrade path: sort the bucket by `hour` from the 24-hour array, or fetch
            // 7 daily rollups instead of re-slicing one day's hourly rows.
            const latestReading = dayReadings.sort((a, b) => new Date(b.label).getTime() - new Date(a.label).getTime())[0];
            chartData.push({
                value: latestReading.humidity,
                label: dayLabel,
                timestamp: new Date(latestReading.label).getTime(),
                error: latestReading.humidity !== null && latestReading.humidity >= 71,
            });
        }
    }
    return chartData;
};
exports.formatHumidityChartData = formatHumidityChartData;
const useLatestHumidityFromEvents = (hubId, sensorId) => {
    const { sensorEvents } = (0, index_1.useSensorEvents)(hubId, sensorId);
    const latestEvent = (0, react_1.useMemo)(() => sensorEvents.find((event) => event.eventType === 'DATA' &&
        event.parsedMetadata?.measurementType === index_1.SensorType.LEAK_DETECTOR), [sensorEvents]);
    // Check if the event exists and is not older than 24 hours
    const isEventRecent = latestEvent &&
        (Date.now() - latestEvent.timestamp * 1000) < 24 * 60 * 60 * 1000; // 24 hours in milliseconds
    const humidity = isEventRecent
        ? (0, index_1.extractReadingValueFromSensorEvent)(latestEvent)?.humidity
        : null;
    return humidity == null ? null : Math.round(humidity);
};
exports.useLatestHumidityFromEvents = useLatestHumidityFromEvents;
const calculateLatestHumidityValue = (humidityData, latestHumidityValueFromEvent) => {
    if (latestHumidityValueFromEvent !== null) {
        return latestHumidityValueFromEvent;
    }
    if (!humidityData || humidityData.length === 0)
        return null;
    const sortedData = [...humidityData].sort((a, b) => b.timestamp - a.timestamp);
    return sortedData[0].value;
};
exports.calculateLatestHumidityValue = calculateLatestHumidityValue;
